import React, { useEffect, useState } from "react";
import { ContentTabsProps, promotionTypes } from "./types";
import { Tabs, TabsList, TabsTrigger } from "../misc/Tabs";
import ListingsTab from "./ListingsTab";
import PromotionAssetsTab from "../promotions/PromotionAssetsTab";
import { Assets } from "../../types/asset-type";
import { Button } from "../misc/Button";
import { FaCircleCheck, FaPencil } from "react-icons/fa6";
import ResourcesTab from "./ResourcesTab";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../listings/slideshow/Tooltip";
import { Info, X } from "lucide-react";
import CustomDialog from "../misc/CustomDialog";
import UploadNavigationWarning from "../misc/UploadNavigationWarning";

const ContentTabs: React.FC<ContentTabsProps> = ({
    promotions,
    activeTab,
    setActiveTab,
    listingMedia,
    listingTabs,
    activeListingTab,
    setActiveListingTab,
    loading,
    mlsApproved,
    carouselApi,
    fetchAssets,
    fetchListingMedia,
    setLoading,
    carouselIndexMap,
    resources,
    fetchResources,
    isMobile,
    activeMediaTab,
    setActiveMediaTab,
    activePromotionType,
    setActivePromotionType,
    activeResourceType,
    setActiveResourceType,
    onThumbnailClick,
    onActiveUploadsChange,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState<Assets[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [activeUploadsCount, setActiveUploadsCount] = useState(0);
    const [showTabChangeWarning, setShowTabChangeWarning] = useState(false);
    const [pendingTab, setPendingTab] = useState<"listing" | "promotion" | "resource" | null>(null);

    // Handle upload count changes from child components
    const handleActiveUploadsChange = (count: number) => {
        setActiveUploadsCount(count);
        // Forward to parent
        if (onActiveUploadsChange) {
            onActiveUploadsChange(count);
        }
    };

    // Reset upload count when changing tabs
    useEffect(() => {
        // When tab changes successfully, clear any pending warnings
        setShowTabChangeWarning(false);
        setPendingTab(null);
    }, [activeTab]);

    useEffect(() => {        
        // If uploads complete while modal is open, close it automatically
        if (activeUploadsCount === 0 && showTabChangeWarning) {
            console.log('✅ ContentTabs: Uploads completed (0) - auto-closing tab change warning');
            setShowTabChangeWarning(false);
            setPendingTab(null);
        }
    }, [activeUploadsCount, showTabChangeWarning]);

    // Additional safety check for modal state
    useEffect(() => {
        if (showTabChangeWarning && activeUploadsCount === 0) {
            setShowTabChangeWarning(false);
            setPendingTab(null);
        }
    }, [showTabChangeWarning, activeUploadsCount]);

    const handleTabChange = (value: string) => {
        const newTab = value as "listing" | "promotion" | "resource";
                
        // Only warn when switching AWAY from a tab with active uploads
        // Check which tab has uploads based on current active tab
        const switchingAwayFromUploads = activeUploadsCount > 0 && 
            activeTab !== newTab && 
            // We only care if leaving the tab that has uploads
            (activeTab === "listing" || activeTab === "promotion" || activeTab === "resource");
        
        if (switchingAwayFromUploads) {
            setPendingTab(newTab);
            setShowTabChangeWarning(true);
        } else {
            setActiveTab(newTab);
        }
    };

    const handleConfirmTabChange = () => {
        sessionStorage.removeItem('uploadsInterrupted');
        if (pendingTab) {
            // Reset upload count to 0 to stop blocking navigation
            setActiveUploadsCount(0);
            if (onActiveUploadsChange) {
                onActiveUploadsChange(0);
            }
            setActiveTab(pendingTab);
            setPendingTab(null);
        }
        setShowTabChangeWarning(false);
    };

    const handleCancelTabChange = () => {
        setPendingTab(null);
        setShowTabChangeWarning(false);
    };

    useEffect(() => {
        setSelectedMedia([]);
    }, [setIsEditing]);

    return (
        <>
        <UploadNavigationWarning
            isOpen={showTabChangeWarning}
            onClose={handleCancelTabChange}
            onLeave={handleConfirmTabChange}
            onStay={handleCancelTabChange}
        />
        <CustomDialog
            open={tooltipVisible}
            onClose={() => setTooltipVisible(false)}
            className="max-w-4xl w-full"
            >
                 <div className="max-w-4xl min-w-4xl bg-white">
                    <h2 className="text-xl font-bold mb-4 text-primary text-center">
                        My Content Tabs
                    </h2>
                    <button
                        className="absolute top-4 right-4 text-gray-500 hover:text-red-500"
                        onClick={() => setTooltipVisible(false)}
                    >
                       <X className="h-6 w-6" />
                    </button>
                    <div className="space-y-4">
                        <p className="mb-1">
                            <strong className="text-primary">Listings:</strong>{" "}
                            Manage user uploaded listing media - images and
                            videos.
                        </p>
                        <p className="mb-1">
                            <strong className="text-primary">Promotion:</strong>{" "}
                            Manage user uploaded promotion images. These images
                            will be prioritized over the Marabot stock library.
                        </p>
                        <p>
                            <strong className="text-primary">
                                Shared Media:
                            </strong>{" "}
                            Manage user uploaded media that is not linked to a
                            listing or promotion campaign, ideal for ad hoc
                            posts, or manual edits to posts.
                        </p>
                    </div>
                </div>
                </CustomDialog>
            <Tabs
                value={activeTab}
                onValueChange={handleTabChange}
                className={`${isMobile ? "w-full" : "w-[1000px] min-h-screen"}`}
            >
                <div
                    className={`flex ${
                        isMobile ? "flex-col" : "flex-row"
                    } justify-between mb-4`}
                >
                     <div className="flex items-center">
                        <TabsList
                            isMobile={isMobile}
                            className={`${isMobile ? "mb-2" : ""}`}
                        >
                            <TabsTrigger id="listings-tab" value="listing">
                                Listings
                            </TabsTrigger>
                            <TabsTrigger id="promotions-tab" value="promotion">
                                Promotion
                            </TabsTrigger>
                            <TabsTrigger id="resources-tab" value="resource">
                                Shared Media
                            </TabsTrigger>
                            {/* Tooltip Icon */}
                            <TooltipProvider>
                                <Tooltip
                                    {...(tooltipVisible
                                        ? {
                                              open: false /* force it closed when dialog is active */,
                                          }
                                        : undefined)}
                                >
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-primary hover:text-black hover:bg-background"
                                            onClick={() =>
                                                setTooltipVisible(true)
                                            }
                                        >
                                            <Info className="h-4 w-4" />
                                            <span className="sr-only">
                                                Click here to get more
                                                information on these tabs.
                                            </span>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent className="text-xs text-white">
                                        Click here to get more information on
                                        these tabs.
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </TabsList>
                    </div>
                    <Button
                        id="edit-done-btn"
                        onClick={() => {
                            setIsEditing(!isEditing);
                            setSelectedMedia([]);
                        }}
                        className={`${isMobile ? "w-full" : ""}`}
                    >
                        {isEditing ? (
                            <FaCircleCheck className="mr-1 w-4 h-4" />
                        ) : (
                            <FaPencil className="mr-1 w-4 h-4" />
                        )}
                        {isEditing ? "Done" : "Edit"}
                    </Button>
                </div>
                <div className="w-full">
                <div className="relative">
                        <ListingsTab
                            listingMedia={listingMedia}
                            listingTabs={listingTabs}
                            activeListingTab={activeListingTab}
                            setActiveListingTab={setActiveListingTab}
                            activeTab={activeTab}
                            loading={loading}
                            mlsApproved={mlsApproved}
                            carouselApi={carouselApi}
                            setLoading={setLoading}
                            fetchListingMedia={fetchListingMedia}
                            carouselIndexMap={carouselIndexMap}
                            isEditing={isEditing}
                            isMobile={isMobile}
                            activeMediaTab={activeMediaTab}
                            setActiveMediaTab={setActiveMediaTab}
                            onThumbnailClick={onThumbnailClick}
                            isUploading={isUploading}
                            setIsUploading={setIsUploading}
                            onActiveUploadsChange={handleActiveUploadsChange}
                        />
                    </div>
                    <div className="w-full">
                        <PromotionAssetsTab
                            isEditing={isEditing}
                            assets={
                                Array.isArray(promotions?.assets)
                                    ? promotions?.assets
                                    : []
                            }
                            promotionTypes={promotionTypes}
                            promotionId={promotions?.id}
                            selectedMedia={selectedMedia}
                            setSelectedMedia={setSelectedMedia}
                            carouselApi={carouselApi}
                            fetchAssets={fetchAssets}
                            activePromotionType={activePromotionType}
                            setActivePromotionType={setActivePromotionType}
                            carouselAssetIndexMap={carouselIndexMap}
                            value="promotion"
                            setLoading={setLoading}
                            isMobile={isMobile}
                            onThumbnailClick={onThumbnailClick}
                            isUploading={isUploading}
                            setIsUploading={setIsUploading}
                            onActiveUploadsChange={handleActiveUploadsChange}
                        />
                    </div>
                    <ResourcesTab
                        resources={resources}
                        isEditing={isEditing}
                       
                        carouselApi={carouselApi}
                        carouselIndexMap={carouselIndexMap}
                        fetchResources={fetchResources}
                        setLoading={setLoading}
                        isMobile={isMobile}
                        activeResourceType={activeResourceType}
                        setActiveResourceType={setActiveResourceType}
                        onThumbnailClick={onThumbnailClick}
                        isUploading={isUploading}
                        setIsUploading={setIsUploading}
                        onActiveUploadsChange={handleActiveUploadsChange}
                    />
                </div>
            </Tabs>
         </>
    );
};

export default ContentTabs;
